// Visited-stack navigation for the respondent runner and the preview.
//
// The runner used to track position as a bare index and move with sel+1 / sel-1.
// Skip logic needs two things a bare index can't give: Back must return to the
// item that was actually shown (not index-1), and "next" must be decided by the
// routing engine rather than always being the adjacent slot. This module models
// position as a stack of the indices actually visited, and takes the "what's
// next" decision as a pluggable resolver.
//
// The runner and the preview pass the route evaluator's resolver (routeResolver);
// the default, the next slot in document order, is the old index walk.

// Skip-logic sections are filtered out upstream and content screens are real
// slots, so "next in document order" is simply the following index. Returns null
// at the end of the list.
export const linearNextIndex = (currentIdx, items) =>
  Array.isArray(items) && currentIdx + 1 < items.length ? currentIdx + 1 : null

// The index currently shown: the top of the visited stack.
export const currentIndex = visited =>
  Array.isArray(visited) && visited.length > 0 ? visited[visited.length - 1] : 0

// Whether there is an earlier item to return to.
export const canGoBack = visited => Array.isArray(visited) && visited.length > 1

// The next index according to the resolver, or null at the end of the path or
// when the resolver returns something out of range.
export const nextIndex = (
  visited,
  items,
  resolveNextIndex = linearNextIndex,
  answers = []
) => {
  if (!Array.isArray(items)) return null
  const next = resolveNextIndex(currentIndex(visited), items, answers)
  if (next == null || next < 0 || next >= items.length) return null
  return next
}

// True when the current item is the last one on the path (so the runner shows
// Submit rather than Next).
export const isLastShown = (
  visited,
  items,
  resolveNextIndex = linearNextIndex,
  answers = []
) => nextIndex(visited, items, resolveNextIndex, answers) == null

// Push the next item onto the stack, or return the stack unchanged at the end
// of the path.
export const advance = (
  visited,
  items,
  resolveNextIndex = linearNextIndex,
  answers = []
) => {
  const next = nextIndex(visited, items, resolveNextIndex, answers)
  return next == null ? visited : [...visited, next]
}

// Pop back to the previous item actually shown, or stay put at the first item.
export const goBack = visited => (canGoBack(visited) ? visited.slice(0, -1) : visited)
